/**
 * 路由配置
 */
import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';

// 懒加载页面组件
import Dashboard from '../../pages/Dashboard';

const Project = React.lazy(() => import('../../pages/Project'));
const WorkTime = React.lazy(() => import('../../pages/WorkTime'));
const Equipment = React.lazy(() => import('../../pages/Equipment'));
const Material = React.lazy(() => import('../../pages/Material'));
const Reports = React.lazy(() => import('../../pages/Reports'));
const DataManagement = React.lazy(() => import('../../pages/DataManagement'));
const PriceConfig = React.lazy(() => import('../../pages/PriceConfig'));
const UserManagement = React.lazy(() => import('../../pages/UserManagement'));
const UserPermissionManage = React.lazy(() => import('../../pages/System/UserPermissionManage'));
const PermissionMatrix = React.lazy(() => import('../../pages/PermissionMatrix'));
const PermissionDenied = React.lazy(() => import('../../pages/PermissionDenied'));
const ResponsiveTest = React.lazy(() => import('../../pages/ResponsiveTest'));
const SyncDemo = React.lazy(() => import('../../pages/SyncDemo'));

// 库存和调拨模块懒加载
const Inventory = React.lazy(() => import('../../pages/Inventory'));
const Transfer = React.lazy(() => import('../../pages/Transfer'));

// 进度跟踪模块页面懒加载
const WorkItemList = React.lazy(() => import('../../modules/progress/pages/WorkItemList'));
const ProgressRecord = React.lazy(() => import('../../modules/progress/pages/ProgressRecord'));
const AdditionList = React.lazy(() => import('../../modules/progress/pages/AdditionList'));
const AdditionDetail = React.lazy(() => import('../../modules/progress/pages/AdditionDetail'));
const RectificationList = React.lazy(() => import('../../modules/progress/pages/RectificationList'));
const RectificationDetail = React.lazy(() => import('../../modules/progress/pages/RectificationDetail'));
const ProgressReportOld = React.lazy(() => import('../../modules/progress/pages/ProgressReport'));
const AdditionReport = React.lazy(() => import('../../modules/progress/pages/AdditionReport'));
const RectificationReport = React.lazy(() => import('../../modules/progress/pages/RectificationReport'));

// 新报表中心页面懒加载
const ReportsHome = React.lazy(() => import('../../pages/ReportsNew'));
const ReportsDashboard = React.lazy(() => import('../../pages/ReportsNew/Dashboard'));
const ReportsCost = React.lazy(() => import('../../pages/ReportsNew/CostReport'));
const ReportsProgress = React.lazy(() => import('../../pages/ReportsNew/ProgressReport'));
const ReportsMaterial = React.lazy(() => import('../../pages/ReportsNew/MaterialReport'));
const ReportsWorkTime = React.lazy(() => import('../../pages/ReportsNew/WorkTimeReport'));
const ReportsInventory = React.lazy(() => import('../../pages/ReportsNew/InventoryReport'));
const ReportsEquipment = React.lazy(() => import('../../pages/ReportsNew/EquipmentReport'));
const ReportsChangeOrder = React.lazy(() => import('../../pages/ReportsNew/ChangeOrderReport'));
const ReportsRectification = React.lazy(() => import('../../pages/ReportsNew/RectificationReport'));
const ReportsTransfer = React.lazy(() => import('../../pages/ReportsNew/TransferReport'));

// 工人管理模块页面懒加载
const WorkerManage = React.lazy(() => import('../../pages/WorkerManage'));
const WorkerDetail = React.lazy(() => import('../../pages/WorkerManage/WorkerDetail'));
const WorkerHistory = React.lazy(() => import('../../pages/WorkerManage/WorkerHistory'));  // 🔥 新增

// 批量语义解析页面懒加载
const BatchSemanticParse = React.lazy(() => import('../../pages/BatchSemanticParse'));

// 工时批量提交历史管理页面懒加载
const WorktimeBatchHistory = React.lazy(() => import('../../pages/WorktimeBatchHistory'));

// 材料批量解析页面懒加载
const BatchMaterialParse = React.lazy(() => import('../../pages/BatchMaterialParse'));

// 数据验证页面懒加载
const DataValidation = React.lazy(() => import('../../pages/DataValidation'));

// 草稿管理页面懒加载
const DraftManagement = React.lazy(() => import('../../pages/DraftManagement'));

// Token 调试页面懒加载
const TokenDebugPage = React.lazy(() => import('../../pages/TokenDebugPage'));

// 图片识别管理页面懒加载
const ImageUploadPage = React.lazy(() => import('../../pages/ImageUploadPage'));
const RecognitionManagementPage = React.lazy(() => import('../../pages/RecognitionManagementPage'));
const RecognitionDetailPage = React.lazy(() => import('../../pages/RecognitionDetailPage'));

// 识别数据审核页面懒加载
const RecognitionReviewPage = React.lazy(() => import('../../pages/RecognitionReviewPage'));

// 供应商管理页面懒加载
const SupplierManagement = React.lazy(() => import('../../pages/SupplierManagement'));

/**
 * 应用路由配置
 */
export const AppRoutes: React.FC = () => {
  return (
    <React.Suspense
      fallback={
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '50vh' 
        }}>
          <div className="loading-spinner" />
        </div>
      }
    >
      <Routes>
        <Route path="/" element={<Dashboard />} />
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/project/*" element={<Project />} />
        <Route path="/project-control-center" element={<Navigate to="/dashboard?tab=control" replace />} />
        <Route path="/worktime/*" element={<WorkTime />} />
        <Route path="/equipment/*" element={<Equipment />} />
        <Route path="/material/*" element={<Material />} />
        <Route path="/inventory/*" element={<Inventory />} />
        <Route path="/transfer/*" element={<Transfer />} />
        <Route path="/reports/*" element={<Reports />} />
        <Route path="/data-management/*" element={<DataManagement />} />
        <Route path="/price-config/*" element={<PriceConfig />} />
        <Route path="/user-management/*" element={<UserManagement />} />
        <Route path="/user-permission-manage" element={<UserPermissionManage />} />
        <Route path="/permission-matrix/*" element={<PermissionMatrix />} />
        <Route path="/permission-denied" element={<PermissionDenied />} />
        <Route path="/responsive-test" element={<ResponsiveTest />} />
        <Route path="/sync-demo" element={<SyncDemo />} />
        {/* 进度跟踪模块路由 */}
        <Route path="/progress/work-items" element={<WorkItemList />} />
        <Route path="/progress/records" element={<ProgressRecord />} />
        <Route path="/progress/additions" element={<AdditionList />} />
        <Route path="/progress/additions/:id" element={<AdditionDetail />} />
        <Route path="/progress/rectifications" element={<RectificationList />} />
        <Route path="/progress/rectifications/:id" element={<RectificationDetail />} />
        <Route path="/progress/reports/progress" element={<ProgressReportOld />} />
        <Route path="/progress/reports/additions" element={<AdditionReport />} />
        <Route path="/progress/reports/rectifications" element={<RectificationReport />} />
        {/* 新报表中心路由 */}
        <Route path="/reports-new" element={<ReportsHome />} />
        <Route path="/reports-new/dashboard" element={<ReportsDashboard />} />
        <Route path="/reports-new/cost" element={<ReportsCost />} />
        <Route path="/reports-new/progress" element={<ReportsProgress />} />
        <Route path="/reports-new/material" element={<ReportsMaterial />} />
        <Route path="/reports-new/worktime" element={<ReportsWorkTime />} />
        <Route path="/reports-new/inventory" element={<ReportsInventory />} />
        <Route path="/reports-new/equipment" element={<ReportsEquipment />} />
        <Route path="/reports-new/change-order" element={<ReportsChangeOrder />} />
        <Route path="/reports-new/rectification" element={<ReportsRectification />} />
        <Route path="/reports-new/transfer" element={<ReportsTransfer />} />
        {/* 工人管理模块路由 */}
        <Route path="/worker-manage" element={<WorkerManage />} />
        <Route path="/worker-manage/:id" element={<WorkerDetail />} />
        <Route path="/worker-history" element={<WorkerHistory />} />  {/* 🔥 新增 */}
        {/* 批量语义解析路由 */}
        <Route path="/batch-semantic-parse" element={<BatchSemanticParse />} />
        {/* 工时批量提交历史路由 */}
        <Route path="/worktime-batch-history" element={<WorktimeBatchHistory />} />
        {/* 材料批量解析路由 */}
        <Route path="/batch-material-parse" element={<BatchMaterialParse />} />
        {/* 数据验证路由 */}
        <Route path="/data-validation" element={<DataValidation />} />
        {/* 草稿管理路由 */}
        <Route path="/draft-management" element={<DraftManagement />} />
        {/* Token 调试路由 */}
        <Route path="/token-debug" element={<TokenDebugPage />} />
        {/* 图片识别管理路由 */}
        <Route path="/image-upload" element={<ImageUploadPage />} />
        <Route path="/recognition-management" element={<RecognitionManagementPage />} />
        <Route path="/recognition-detail/:id" element={<RecognitionDetailPage />} />
        {/* 识别数据审核路由 */}
        <Route path="/recognition-review" element={<RecognitionReviewPage />} />
        {/* 供应商管理路由 */}
        <Route path="/supplier-management" element={<SupplierManagement />} />
      </Routes>
    </React.Suspense>
  );
};
